import React, { useCallback, useMemo } from 'react';
import type { EvaluationBlock, EvaluationExam } from '@interactive-room';
import {
  normalizeGuideContextBlocks,
  normalizeGuideCompletarBlocks,
  normalizeGuideStimulusBlocks,
  enforceCompletarGuideBlocks,
  enforceHumanitiesGuideBlocks,
  inferStudyGuide,
  prepareStudyGuideBlocks,
} from '@interactive-room';
import { PrintBlockSection } from './PrintBlockSection';
import { PrintBlockGrid } from './PrintBlockGrid';
import { PrintBlockInsertGap } from './PrintBlockInsertGap';
import { groupPrintBlockRows } from './printBlockRows';
import { GuidePrintHeader } from './GuidePrintHeader';
import { DELIVERY_PROFILE_LABELS } from '../constants/deliveryProfile';
import { deliveryProfileFromExam } from '../constants/studioMaterialRoutes';
import { StaggerReveal } from '../../components/ui/StaggerReveal';
import { resolvePrintBlockRole } from './printBlockRole';
import {
  pedagogicalMomentSectionLabel,
  resolvePrintPedagogicalMoment,
  shouldShowMomentSectionDivider,
} from './printPedagogicalMoment';
import type { ExamBlockExamContext } from '../components/ExamBlockInlineEditor';
import { buildGuideContextFromBlocks } from '../constants/blockAiParams';
import { SUBJECT_CATALOG } from '../constants/subjectCatalog';
import { resolveExamAiLevel } from '../lib/resolveExamAiIdentity';
import { getCourseProfileById } from '../services/courseProfileStorage';

interface PrintWorksheetDocumentProps {
  exam: EvaluationExam;
  subjectName?: string;
  subjectTag?: string;
  revealStagger?: boolean;
  /** Nombre del colegio — masthead arriba-izquierda (sin Edusico). */
  schoolName?: string;
  /** Activa hover Editar / IA en cada bloque (vista impresión WYSIWYG). */
  editable?: boolean;
  onBlockChange?: (block: EvaluationBlock) => void;
  /** Insertar bloque en índice (reindexa el exam en el padre). */
  onInsertBlock?: (block: EvaluationBlock, insertAt: number) => void;
}

export function PrintWorksheetDocument({
  exam,
  subjectName,
  subjectTag,
  revealStagger = false,
  schoolName,
  editable = false,
  onBlockChange,
  onInsertBlock,
}: PrintWorksheetDocumentProps) {
  const studyGuide =
    exam.deliveryProfile === 'learning_guide' ? false : inferStudyGuide(exam);
  const isQuestionnaire = String(exam.focus || '').toLowerCase() === 'inquiry';
  // Cuestionario usa pipeline guide pero no el chrome teach→practice de guía.
  const isLearningGuide =
    !isQuestionnaire &&
    (exam.deliveryProfile === 'learning_guide' ||
      (exam.resourceType === 'guide' && !studyGuide));

  const printExam = useMemo(() => {
    let base =
      exam.resourceType === 'guide' && !studyGuide
        ? normalizeGuideStimulusBlocks(
            enforceCompletarGuideBlocks(
              normalizeGuideCompletarBlocks(
                normalizeGuideContextBlocks(
                  exam as EvaluationExam & {
                    blocks: Array<{ mode: string; payload: Record<string, unknown> }>;
                  },
                ),
              ),
            ),
          )
        : exam;

    if (exam.resourceType === 'guide' && !studyGuide && base.blocks.length > 0) {
      base = {
        ...base,
        blocks: enforceHumanitiesGuideBlocks(base.blocks, subjectTag ?? exam.subjectTag),
      };
    }

    if (studyGuide && base.blocks.length > 1) {
      base = {
        ...base,
        isStudyGuide: true,
        blocks: prepareStudyGuideBlocks(base.blocks, subjectTag ?? exam.subjectTag).map((block, index) => ({
          ...block,
          index,
        })),
      };
    }

    return base;
  }, [exam, studyGuide, subjectTag]);

  const printRows = useMemo(
    () => (studyGuide ? groupPrintBlockRows(printExam.blocks) : null),
    [printExam.blocks, studyGuide],
  );

  const pedagogicalMoments = useMemo(() => {
    if (!isLearningGuide) return [];
    return printExam.blocks.map((block, index) =>
      resolvePrintPedagogicalMoment(
        printExam.blocks,
        index,
        String((block as { _blueprintPurpose?: string })._blueprintPurpose || ''),
      ),
    );
  }, [isLearningGuide, printExam.blocks]);

  const practiceNumbers = useMemo(() => {
    let count = 0;
    return printExam.blocks.map((block) => {
      if (resolvePrintBlockRole(block.mode) === 'practice') {
        count += 1;
        return count;
      }
      return undefined;
    });
  }, [printExam.blocks]);

  const baseExamContext = useMemo((): Omit<ExamBlockExamContext, 'blockIndex' | 'guideContext'> => {
    const tag = subjectTag ?? exam.subjectTag;
    const subject = SUBJECT_CATALOG.find((s) => s.tag === tag || s.id === tag);
    const courseId = exam.courseId || exam.linkedSession?.courseId;
    const courseLevel = courseId ? getCourseProfileById(courseId)?.level : undefined;
    const level = resolveExamAiLevel(exam, courseLevel);
    return {
      examTitle: exam.title,
      subjectTag: tag,
      resourceType: exam.resourceType ?? 'exam',
      subjectExpertPrompt: subject?.aiExpertPrompt,
      level,
    };
  }, [exam, subjectTag]);

  const examContextForIndex = useCallback(
    (index: number): ExamBlockExamContext => ({
      ...baseExamContext,
      blockIndex: index,
      guideContext:
        exam.resourceType === 'guide' || isLearningGuide
          ? buildGuideContextFromBlocks(exam.blocks, index)
          : undefined,
    }),
    [baseExamContext, exam.blocks, exam.resourceType, isLearningGuide],
  );

  const deliveryProfile = deliveryProfileFromExam(exam);
  const practiceCount = practiceNumbers.filter((n) => typeof n === 'number').length;
  const docType = studyGuide
    ? 'Apunte · Atlas'
    : isQuestionnaire
      ? 'Cuestionario de práctica'
    : deliveryProfile === 'learning_guide'
      ? DELIVERY_PROFILE_LABELS.learning_guide
      : deliveryProfile === 'interactive_class'
        ? DELIVERY_PROFILE_LABELS.interactive_class
        : exam.resourceType === 'guide'
          ? 'Guía de aprendizaje'
          : DELIVERY_PROFILE_LABELS.assessment;

  return (
    <article
      className={`print-worksheet${studyGuide ? ' print-worksheet--study-guide' : ''}${
        isLearningGuide ? ' print-worksheet--learning-guide' : ''
      }${editable ? ' print-worksheet--editable' : ''}`}
    >
      <GuidePrintHeader
        exam={printExam}
        subjectName={subjectName}
        docType={docType}
        studyGuide={studyGuide}
        schoolName={schoolName}
      />

      <div className="print-worksheet__blocks">
        {studyGuide && printRows ? (
          <PrintBlockGrid
            rows={printRows}
            subjectTag={subjectTag ?? exam.subjectTag}
            isStudyGuide={studyGuide}
            editable={editable}
            revealStagger={revealStagger}
            examContextForIndex={examContextForIndex}
            onBlockChange={onBlockChange}
          />
        ) : (
          <>
            {editable && onInsertBlock ? (
              <PrintBlockInsertGap exam={exam} insertAt={0} onInsert={onInsertBlock} />
            ) : null}
            {printExam.blocks.map((block, index) => (
              <React.Fragment key={block.id}>
                <StaggerReveal index={index} enabled={revealStagger}>
                  {isLearningGuide &&
                  shouldShowMomentSectionDivider(pedagogicalMoments, index) ? (
                    (() => {
                      const section = pedagogicalMomentSectionLabel(pedagogicalMoments[index]!);
                      if (!section) return null;
                      return (
                        <div
                          className={`print-moment-divider print-moment-divider--${pedagogicalMoments[index]}`}
                          aria-label={section.label}
                        >
                          <span className="print-moment-divider__label">{section.label}</span>
                          <span className="print-moment-divider__hint">{section.hint}</span>
                        </div>
                      );
                    })()
                  ) : null}
                  <PrintBlockSection
                    block={block}
                    index={index}
                    subjectTag={subjectTag ?? exam.subjectTag}
                    isStudyGuide={studyGuide}
                    isLearningGuide={isLearningGuide}
                    blockRole={isLearningGuide ? resolvePrintBlockRole(block.mode) : 'neutral'}
                    pedagogicalMoment={isLearningGuide ? pedagogicalMoments[index] : undefined}
                    practiceNumber={practiceNumbers[index]}
                    editable={editable}
                    examContext={examContextForIndex(index)}
                    onBlockChange={onBlockChange}
                  />
                </StaggerReveal>
                {editable && onInsertBlock ? (
                  <PrintBlockInsertGap
                    exam={exam}
                    insertAt={index + 1}
                    onInsert={onInsertBlock}
                  />
                ) : null}
              </React.Fragment>
            ))}
          </>
        )}
      </div>

      <footer className="print-worksheet__footer">
        <p>
          {studyGuide
            ? `${printExam.blocks.length} secciones · Material de estudio`
            : isLearningGuide
              ? `${practiceCount} actividades`
              : `${printExam.blocks.length} actividades`}
        </p>
        <p className="print-worksheet__footer-brand">Edusico</p>
      </footer>
    </article>
  );
}
